import { Home, Building2, Factory, Wheat, Battery, Sun, LineChart, Leaf } from 'lucide-react';
import type { UserType, UserTypeData } from '../types';
import { Footer } from './Footer';

interface UserTypeSelectorProps {
  onSelect: (type: UserType) => void;
}

const userTypes: UserTypeData[] = [
  {
    type: 'residential',
    title: 'Residential',
    description: 'Smart home energy solutions with attractive ROI and increased property value',
    image: 'https://images.unsplash.com/photo-1513584684374-8bab748fbf90?auto=format&fit=crop&w=2000&q=80',
    icon: 'Home',
    benefits: [
      'Reduce monthly bills by up to 90%',
      'Increase property value by 4.1%',
      'Lock in energy rates for 25+ years'
    ]
  },
  {
    type: 'commercial',
    title: 'Commercial',
    description: 'Comprehensive energy solutions for businesses seeking sustainability and cost reduction',
    image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=1200&q=80',
    icon: 'Building2',
    benefits: [
      'Reduce operating costs significantly',
      'Enhanced corporate ESG profile',
      'Federal & state tax incentives'
    ]
  },
  {
    type: 'industrial',
    title: 'Industrial',
    description: 'High-performance energy solutions for manufacturing and large-scale operations',
    image: 'https://images.unsplash.com/photo-1516937941344-00b4e0337589?auto=format&fit=crop&w=1200&q=80',
    icon: 'Factory',
    benefits: [
      'Custom industrial-scale solutions',
      'Peak demand management',
      'Energy independence'
    ]
  },
  {
    type: 'agricultural',
    title: 'Agricultural',
    description: 'Sustainable energy solutions optimized for agricultural operations',
    image: 'https://images.unsplash.com/photo-1500382017468-9049fed747ef?auto=format&fit=crop&w=1200&q=80',
    icon: 'Wheat',
    benefits: [
      'Irrigation system optimization',
      'USDA REAP grant eligible',
      'Drought resilience'
    ]
  },
];


const IconComponent = {
  Home,
  Building2,
  Factory,
  Wheat,
};

export function UserTypeSelector({ onSelect }: UserTypeSelectorProps) {

  const handleTypeSelect = (type: UserType) => {
    // Simply pass the selected type to the parent component
    onSelect(type);
  };

  return (
    <div className="max-w-7xl mx-auto px-4">
      <div className="text-center mb-8 sm:mb-12">
        <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
          Energy Solutions for Every Need
        </h2>
        <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto">
          Whether you're a homeowner, business owner, or developer, our advanced AI design system 
          creates customized energy solutions that maximize your ROI and sustainability goals.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 mb-12 sm:mb-16">
        {userTypes.map((type) => {
          const Icon = IconComponent[type.icon as keyof typeof IconComponent];
          return (
            <div
              key={type.type}
              className="group relative overflow-hidden rounded-3xl cursor-pointer transform transition-all duration-300 hover:scale-[1.02] min-h-[400px] sm:min-h-[550px]"
              onClick={() => handleTypeSelect(type.type)}
            >
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-black/20 z-10" />
              <img
                src={type.image}
                alt={type.title}
                className="absolute inset-0 w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-300"
              />
              <div className="relative z-20 p-6 h-full flex flex-col text-white">
                {/* Icon at the top */}
                <div className="bg-white/10 w-12 h-12 rounded-full flex items-center justify-center mb-4 backdrop-blur-sm flex-shrink-0">
                  <Icon className="h-6 w-6" />
                </div>
                
                {/* Content area that grows */}
                <div className="flex-1 flex flex-col justify-between">
                  <div>
                    <h3 className="text-2xl font-bold mb-3">{type.title}</h3>
                    <p className="text-white/90 mb-4 text-sm leading-relaxed">{type.description}</p>
                    <ul className="space-y-2 mb-6">
                      {type.benefits.map((benefit, index) => (
                        <li key={index} className="flex items-start text-white/80 text-sm">
                          <div className="h-1.5 w-1.5 bg-green-400 rounded-full mr-2 mt-2 flex-shrink-0" />
                          <span className="leading-relaxed">{benefit}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  {/* Button at the bottom */}
                  <button className="w-full bg-white/20 backdrop-blur-sm text-white py-3 px-4 rounded-lg hover:bg-white/30 transition-colors duration-200 font-medium mt-auto">
                    Get Started
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="bg-white rounded-3xl shadow-lg p-6 sm:p-8 mb-12 sm:mb-16 border border-gray-200">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
          <div className="text-center">
            <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <Sun className="h-8 w-8 text-green-600" />
            </div>
            <h4 className="text-xl font-bold text-gray-900 mb-2">AI-Powered Design</h4>
            <p className="text-gray-600">Intelligent energy design optimization for maximum efficiency</p>
          </div>
          <div className="text-center">
            <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <Battery className="h-8 w-8 text-blue-600" />
            </div>
            <h4 className="text-xl font-bold text-gray-900 mb-2">Energy Storage</h4>
            <p className="text-gray-600">Integrated battery solutions for 24/7 power reliability</p>
          </div>
          <div className="text-center">
            <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <LineChart className="h-8 w-8 text-purple-600" />
            </div>
            <h4 className="text-xl font-bold text-gray-900 mb-2">ROI Analysis</h4>
            <p className="text-gray-600">Detailed financial modeling and performance forecasting</p>
          </div>
          <div className="text-center">
            <div className="bg-amber-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <Leaf className="h-8 w-8 text-amber-600" />
            </div>
            <h4 className="text-xl font-bold text-gray-900 mb-2">Sustainability</h4>
            <p className="text-gray-600">Reduce carbon footprint and meet ESG goals</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-8 text-center">
        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-6 sm:p-8">
          <div className="text-2xl sm:text-3xl font-bold text-green-600 mb-2">30%</div>
          <p className="text-green-800">Government Subsidy</p>
          <p className="text-sm text-green-600 mt-2">Plus additional incentives</p>
        </div>
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-6 sm:p-8">
          <div className="text-2xl sm:text-3xl font-bold text-blue-600 mb-2">25+ Years</div>
          <p className="text-blue-800">Performance Warranty</p>
          <p className="text-sm text-blue-600 mt-2">Industry-leading protection</p>
        </div>
        <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl p-6 sm:p-8">
          <div className="text-2xl sm:text-3xl font-bold text-purple-600 mb-2">5-8 Years</div>
          <p className="text-purple-800">Average Payback Period</p>
          <p className="text-sm text-purple-600 mt-2">Excellent ROI potential</p>
        </div>
      </div>
      
      <div className="mt-12">
        <Footer />
      </div>
    </div>
  );
}
